import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { UnifiedChartData } from "./types";
import { formatNumber } from "../../../api/DataTypeUtils";
import { colors } from "../../../board/components/TaskDetail/ColorPicker";

type Props = {
  data: UnifiedChartData;
  lineColors?: string[];
  mode?: "area" | "line";
  height?: number;
};

const LineAreaChart = ({ data, lineColors = colors, mode = "area", height = 440 }: Props) => {
  const rows = data.labels.map((label, i) => {
    const row: any = { label };
    data.series.forEach((s) => {
      row[s.dataKey] = s.data[i] ?? 0;
    });
    return row;
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="recharts-default-tooltip px-3 py-2 rounded-md bg-[#111827]">
          <p className="text-[#D1D5DB]">{label}</p>
          {payload.map((item, index) => (
            <p key={`item-${index}`} className="text-[#ffffff]">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: item?.color || colors[0] }}
              ></span>
              <span className="ml-2">
                {index === 0 ? "Current" : "Previous"}: {formatNumber(item?.value || 0)}
              </span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (mode === "line") {
    return (
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={rows} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="#F3F4F6" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
            tickLine={false}
            axisLine={{ stroke: "#E5E7EB" }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
            tickLine={false}
            axisLine={{ stroke: "#E5E7EB" }}
            tickFormatter={(value) => formatNumber(value)}
          />
          <Tooltip content={CustomTooltip} />
          {data.series.map((s, idx) => (
            <Line
              key={s.name}
              type="monotone"
              strokeWidth={2}
              dataKey={s.dataKey}
              name={s.name}
              stroke={s.color || lineColors[idx % lineColors.length]}
              dot={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={rows} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
        <defs>
          {data.series.map((s, idx) => (
            <linearGradient key={idx} id={`grad-${idx}`} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={s.color || lineColors[idx % lineColors.length]}
                stopOpacity={0.35}
              />
              <stop
                offset="95%"
                stopColor={s.color || lineColors[idx % lineColors.length]}
                stopOpacity={0.05}
              />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid stroke="#F3F4F6" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
          tickLine={false}
          axisLine={{ stroke: "#E5E7EB" }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: "#6B7280", fontWeight: 500 }}
          tickLine={false}
          axisLine={{ stroke: "#E5E7EB" }}
          tickFormatter={(value) => formatNumber(value)}
        />
        <Tooltip content={CustomTooltip} />
        {data.series.map((s, idx) => (
          <Area
            key={s.name}
            type="monotone"
            dataKey={s.dataKey}
            name={s.name}
            strokeWidth={2}
            stroke={s.color || lineColors[idx % lineColors.length]}
            fill={`url(#grad-${idx})`}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default LineAreaChart;
